import React, { useState, useEffect } from 'react';

/**
 * AddressBookManager component for managing customer addresses
 * Provides CRUD operations for address book entries
 */
const AddressBookManager = ({ 
  isOpen, 
  onClose,
  onAddressSelected 
}) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState('');
  const API_URL = process.env.REACT_APP_API_URL || '';

  // Load addresses when component mounts
  useEffect(() => {
    if (isOpen) {
      loadAddresses();
    }
  }, [isOpen]);

  const loadAddresses = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/address-book`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to load address book');
      }
      
      const data = await response.json();
      setAddresses(data);
    } catch (err) {
      setError('Failed to load address book');
      console.error('Error loading addresses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async (addressData) => {
    try {
      const response = await fetch(`${API_URL}/api/address-book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(addressData),
      });

      if (!response.ok) {
        throw new Error('Failed to add address');
      }

      await loadAddresses();
      setShowAddForm(false);
    } catch (err) {
      setError('Failed to add address');
      console.error('Error adding address:', err);
    }
  };

  const handleUpdateAddress = async (addressId, addressData) => {
    try {
      const response = await fetch(`${API_URL}/api/address-book/${addressId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(addressData),
      });

      if (!response.ok) {
        throw new Error('Failed to update address');
      }

      await loadAddresses();
      setEditingAddress(null);
    } catch (err) {
      setError('Failed to update address');
      console.error('Error updating address:', err);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/address-book/${addressId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete address');
      }

      await loadAddresses();
    } catch (err) {
      setError('Failed to delete address');
      console.error('Error deleting address:', err);
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const response = await fetch(`${API_URL}/api/address-book/${addressId}/set-default`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to set default address');
      }

      await loadAddresses();
    } catch (err) {
      setError('Failed to set default address');
      console.error('Error setting default address:', err);
    }
  };

  const AddressForm = ({ address = null, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      customer_name: address?.customer_name || '',
      company_name: address?.company_name || '',
      address_line1: address?.address_line1 || '',
      address_line2: address?.address_line2 || '',
      city: address?.city || '',
      postcode: address?.postcode || '',
      delivery_instructions: address?.delivery_instructions || '',
      is_default: address?.is_default || false,
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Customer Name *</label>
            <input
              type="text"
              value={formData.customer_name}
              onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Company Name</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({...formData, company_name: e.target.value})}
              className="w-full p-2 border rounded"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Address Line 1 *</label>
          <input
            type="text"
            value={formData.address_line1}
            onChange={(e) => setFormData({...formData, address_line1: e.target.value})}
            className="w-full p-2 border rounded"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Address Line 2</label>
          <input
            type="text"
            value={formData.address_line2}
            onChange={(e) => setFormData({...formData, address_line2: e.target.value})}
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">City *</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Postcode *</label>
            <input
              type="text"
              value={formData.postcode}
              onChange={(e) => setFormData({...formData, postcode: e.target.value})}
              className="w-full p-2 border rounded"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Delivery Instructions</label>
          <textarea
            value={formData.delivery_instructions}
            onChange={(e) => setFormData({...formData, delivery_instructions: e.target.value})}
            className="w-full p-2 border rounded"
            rows="2"
            placeholder="Special delivery instructions, safe place, etc."
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_default"
            checked={formData.is_default}
            onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
            className="mr-2"
          />
          <label htmlFor="is_default" className="text-sm">
            Set as default address
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {address ? 'Update Address' : 'Add Address'}
          </button>
        </div>
      </form>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Address Book Manager</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
              {error}
            </div>
          )}

          {showAddForm ? (
            <div>
              <h3 className="text-lg font-medium mb-4">Add New Customer Address</h3>
              <AddressForm
                onSubmit={handleAddAddress}
                onCancel={() => setShowAddForm(false)}
              />
            </div>
          ) : editingAddress ? (
            <div>
              <h3 className="text-lg font-medium mb-4">Edit Customer Address</h3>
              <AddressForm
                address={editingAddress}
                onSubmit={(data) => handleUpdateAddress(editingAddress.id, data)}
                onCancel={() => setEditingAddress(null)}
              />
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Your Customer Addresses</h3>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Add New Address
                </button>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-500">
                  Loading addresses...
                </div>
              ) : addresses.length > 0 ? (
                <div className="space-y-3">
                  {addresses.map(address => (
                    <div key={address.id} className="border rounded p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">
                              {address.customer_name}
                            </h4>
                            {Boolean(address.is_default) && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                Default
                              </span>
                            )}
                          </div>
                          {address.company_name && (
                            <p className="text-sm text-gray-600">{address.company_name}</p>
                          )}
                          <div className="text-sm text-gray-700 mt-1">
                            {address.address_line1}
                            {address.address_line2 && <br />}
                            {address.address_line2}
                            <br />
                            {address.city}, {address.postcode}
                          </div>
                          {address.delivery_instructions && (
                            <p className="text-xs text-gray-500 mt-1">
                              📝 {address.delivery_instructions}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => setEditingAddress(address)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Edit
                          </button>
                          {!address.is_default && (
                            <button
                              onClick={() => handleSetDefault(address.id)}
                              className="text-green-600 hover:text-green-800 text-sm"
                            >
                              Set Default
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteAddress(address.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No addresses found. Add your first customer address to get started.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressBookManager;
